/**
 * Combined documents: one file from many Base records.
 *
 * A template's record mode decides what one press of Generate makes:
 * - `per_record` — one document per record (the default, and every template before this)
 * - `list` — one document; the records are the `{#Records}` loop
 * - `continuous` — one document; the whole template once per record, each on a new page
 *   unless the template asks to flow on
 * - `labels` — one document; the template body is one label, records fill a grid of them
 *
 * A combined run is one generation: one `generation_runs` row, one file, one unit of quota.
 * The API, the extension and the docs read these rules from here, and the generation
 * worker (which cannot import this package) receives the resolved layout in the job.
 */

import type { AutoGenerateTemplateRequest, StarterLayout } from './api'

export const RECORD_MODES = ['per_record', 'list', 'continuous', 'labels'] as const
export type RecordMode = typeof RECORD_MODES[number]
export type CombinedRecordMode = Exclude<RecordMode, 'per_record'>

/**
 * Most records one combined document takes. A list of 500 rows is already 15 pages of
 * table; past that the one PDF conversion is what fails, and a failed conversion is a
 * document the customer waited minutes for and did not get.
 */
export const MAX_COMBINED_RECORDS = 500

export function isRecordMode(value: unknown): value is RecordMode {
  return typeof value === 'string' && (RECORD_MODES as readonly string[]).includes(value)
}

/** The stored mode, with anything unknown (a NULL from before migration 0054) read as per_record */
export function recordModeOf(value: unknown): RecordMode {
  return isRecordMode(value) ? value : 'per_record'
}

/**
 * What a template actually makes. Combined documents are built in the DOCX, so an Excel
 * or PowerPoint template, and an approval template (a document is about one request),
 * make a document per record whatever the column says. The API, the Generate button and
 * the Variables screen all ask here.
 */
export function effectiveRecordMode(template: { record_mode?: unknown; template_format?: unknown; source_type?: unknown }): RecordMode {
  if (template.source_type === 'approval') return 'per_record'
  if ((template.template_format ?? 'docx') !== 'docx') return 'per_record'
  return recordModeOf(template.record_mode)
}

export function isCombinedMode(mode: RecordMode): mode is CombinedRecordMode {
  return mode !== 'per_record'
}

/**
 * The starter a record mode needs (`options.layout` of auto-generate): a combined template's
 * starter keeps record fields out of the shared header, a list's is a table, a labels' one
 * label. Without it a starter for a combined template warns on every run.
 */
export function starterLayoutOf(mode: RecordMode): StarterLayout {
  return mode === 'per_record' ? 'record' : mode
}

/**
 * The `options` of an auto-generate request as the clients build them (Base extension, Word
 * add-in, Excel pane, Sheets add-on): the page word, the layout, and for a list the words it
 * prints, all in the caller's language. The company portal's starter route takes the layout
 * from the stored template on the server instead (authoring.ts).
 */
export function starterRequestOptions(
  layout: StarterLayout,
  words: { page: string; total: string; records: string },
  totalVariables?: string[],
): NonNullable<AutoGenerateTemplateRequest['options']> {
  return {
    footerText: words.page,
    ...(layout !== 'record' ? { layout } : {}),
    ...(layout === 'list' && totalVariables?.length ? { totalVariables } : {}),
    ...(layout === 'list' ? { words: { total: words.total, records: words.records } } : {}),
  }
}

// =============================================================================
// Labels
// =============================================================================

/**
 * A sheet of labels, in millimetres. The label size is not stored: it is what the page
 * leaves once margins and gaps are taken, so a layout can never ask for labels that do
 * not fit the sheet it names.
 */
export interface LabelLayout {
  pageWidthMm: number
  pageHeightMm: number
  columns: number
  rows: number
  marginTopMm: number
  marginBottomMm: number
  marginLeftMm: number
  marginRightMm: number
  /** Between two labels side by side */
  gapXMm: number
  /** Between two labels one above the other */
  gapYMm: number
}

export const LABEL_PRESET_IDS = ['a4_3x8', 'a4_3x7', 'a4_2x7', 'letter_3x10', 'letter_2x5', 'custom'] as const
export type LabelPresetId = typeof LABEL_PRESET_IDS[number]

const A4 = { pageWidthMm: 210, pageHeightMm: 297 }
const LETTER = { pageWidthMm: 215.9, pageHeightMm: 279.4 }

/**
 * Common sheets, named by their grid. The measurements are those of the stock most sold
 * in that grid (63.5 × 33.9 mm for A4 3×8, 2⅝ × 1 in for Letter 3×10, …) so a customer
 * who bought "24 per sheet" labels gets something that lines up without measuring.
 */
export const LABEL_PRESETS: Record<Exclude<LabelPresetId, 'custom'>, LabelLayout> = {
  a4_3x8: { ...A4, columns: 3, rows: 8, marginTopMm: 12.9, marginBottomMm: 12.9, marginLeftMm: 7.25, marginRightMm: 7.25, gapXMm: 2.5, gapYMm: 0 },
  a4_3x7: { ...A4, columns: 3, rows: 7, marginTopMm: 15.15, marginBottomMm: 15.15, marginLeftMm: 7.25, marginRightMm: 7.25, gapXMm: 2.5, gapYMm: 0 },
  a4_2x7: { ...A4, columns: 2, rows: 7, marginTopMm: 15.15, marginBottomMm: 15.15, marginLeftMm: 4.65, marginRightMm: 4.65, gapXMm: 2.5, gapYMm: 0 },
  letter_3x10: { ...LETTER, columns: 3, rows: 10, marginTopMm: 12.7, marginBottomMm: 12.7, marginLeftMm: 4.76, marginRightMm: 4.76, gapXMm: 3.18, gapYMm: 0 },
  letter_2x5: { ...LETTER, columns: 2, rows: 5, marginTopMm: 12.7, marginBottomMm: 12.7, marginLeftMm: 3.97, marginRightMm: 3.97, gapXMm: 4.76, gapYMm: 0 },
}

export const DEFAULT_LABEL_PRESET: Exclude<LabelPresetId, 'custom'> = 'a4_3x8'

/** Bounds a layout must keep. A label under 10 mm holds nothing legible, not even a QR code. */
const LABEL_LIMITS = {
  pageMm: { min: 50, max: 1000 },
  columns: { min: 1, max: 10 },
  rows: { min: 1, max: 40 },
  marginMm: { min: 0, max: 100 },
  gapMm: { min: 0, max: 50 },
  labelMm: { min: 10 },
} as const

/** The size of one label on this sheet */
export function labelSize(layout: LabelLayout): { widthMm: number; heightMm: number } {
  const widthMm = (layout.pageWidthMm - layout.marginLeftMm - layout.marginRightMm - (layout.columns - 1) * layout.gapXMm) / layout.columns
  const heightMm = (layout.pageHeightMm - layout.marginTopMm - layout.marginBottomMm - (layout.rows - 1) * layout.gapYMm) / layout.rows
  return { widthMm, heightMm }
}

export type LabelLayoutProblem =
  | 'not_a_number'
  | 'page_size'
  | 'columns'
  | 'rows'
  | 'margins'
  | 'gaps'
  | 'label_too_small'

/** Why this layout cannot be printed, or null when it can */
export function labelLayoutProblem(layout: LabelLayout): LabelLayoutProblem | null {
  const numbers = [
    layout.pageWidthMm, layout.pageHeightMm, layout.columns, layout.rows,
    layout.marginTopMm, layout.marginBottomMm, layout.marginLeftMm, layout.marginRightMm,
    layout.gapXMm, layout.gapYMm,
  ]
  if (numbers.some(n => typeof n !== 'number' || !Number.isFinite(n))) return 'not_a_number'
  const within = (n: number, b: { min: number; max: number }) => n >= b.min && n <= b.max
  if (!within(layout.pageWidthMm, LABEL_LIMITS.pageMm) || !within(layout.pageHeightMm, LABEL_LIMITS.pageMm)) return 'page_size'
  if (!Number.isInteger(layout.columns) || !within(layout.columns, LABEL_LIMITS.columns)) return 'columns'
  if (!Number.isInteger(layout.rows) || !within(layout.rows, LABEL_LIMITS.rows)) return 'rows'
  if (![layout.marginTopMm, layout.marginBottomMm, layout.marginLeftMm, layout.marginRightMm].every(m => within(m, LABEL_LIMITS.marginMm))) return 'margins'
  if (!within(layout.gapXMm, LABEL_LIMITS.gapMm) || !within(layout.gapYMm, LABEL_LIMITS.gapMm)) return 'gaps'
  const { widthMm, heightMm } = labelSize(layout)
  if (widthMm < LABEL_LIMITS.labelMm.min || heightMm < LABEL_LIMITS.labelMm.min) return 'label_too_small'
  return null
}

// =============================================================================
// Stored settings
// =============================================================================

/**
 * `templates.combined_config` — what a combined mode needs beyond its name. Kept for every
 * mode, so switching a template to per_record and back does not lose a label layout.
 */
export interface CombinedConfig {
  /** continuous: start each record on a new page (default). False flows on, for slips. */
  pageBreak?: boolean
  /** labels: the preset the layout came from, so the settings screen can show it */
  labelPreset?: LabelPresetId
  /** labels: the sheet; absent means the default preset */
  labels?: LabelLayout
}

/** Parse the stored JSON. Anything unreadable is the defaults: a setting is never a reason not to generate. */
export function parseCombinedConfig(json: string | null | undefined): CombinedConfig {
  if (!json) return {}
  try {
    const value = JSON.parse(json) as unknown
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    const raw = value as Record<string, unknown>
    const config: CombinedConfig = {}
    if (typeof raw.pageBreak === 'boolean') config.pageBreak = raw.pageBreak
    if (typeof raw.labelPreset === 'string' && (LABEL_PRESET_IDS as readonly string[]).includes(raw.labelPreset)) {
      config.labelPreset = raw.labelPreset as LabelPresetId
    }
    const labels = readLabelLayout(raw.labels)
    if (labels && labelLayoutProblem(labels) === null) config.labels = labels
    return config
  } catch {
    return {}
  }
}

function readLabelLayout(value: unknown): LabelLayout | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const keys: Array<keyof LabelLayout> = [
    'pageWidthMm', 'pageHeightMm', 'columns', 'rows',
    'marginTopMm', 'marginBottomMm', 'marginLeftMm', 'marginRightMm', 'gapXMm', 'gapYMm',
  ]
  const layout = {} as LabelLayout
  for (const key of keys) {
    if (typeof raw[key] !== 'number') return null
    layout[key] = raw[key] as number
  }
  return layout
}

/**
 * The settings a request may store, or the problem with them. Used by the template update
 * route; the extension runs the same check to explain it before saving.
 */
export function validateCombinedConfig(value: unknown): { config: CombinedConfig } | { problem: LabelLayoutProblem | 'invalid' } {
  if (value === null || value === undefined) return { config: {} }
  if (typeof value !== 'object' || Array.isArray(value)) return { problem: 'invalid' }
  const raw = value as Record<string, unknown>
  const config: CombinedConfig = {}
  if (raw.pageBreak !== undefined) {
    if (typeof raw.pageBreak !== 'boolean') return { problem: 'invalid' }
    config.pageBreak = raw.pageBreak
  }
  if (raw.labelPreset !== undefined) {
    if (typeof raw.labelPreset !== 'string' || !(LABEL_PRESET_IDS as readonly string[]).includes(raw.labelPreset)) return { problem: 'invalid' }
    config.labelPreset = raw.labelPreset as LabelPresetId
  }
  if (raw.labels !== undefined) {
    const labels = readLabelLayout(raw.labels)
    if (!labels) return { problem: 'not_a_number' }
    const problem = labelLayoutProblem(labels)
    if (problem) return { problem }
    config.labels = labels
  }
  return { config }
}

/** The sheet a labels template prints on: its own layout, its preset's, or the default */
export function effectiveLabelLayout(config: CombinedConfig): LabelLayout {
  if (config.labels) return config.labels
  const preset = config.labelPreset && config.labelPreset !== 'custom' ? config.labelPreset : DEFAULT_LABEL_PRESET
  return LABEL_PRESETS[preset]
}

// =============================================================================
// Requests
// =============================================================================

export const COMBINED_TOO_MANY_RECORDS = 'COMBINED_TOO_MANY_RECORDS'

/**
 * Null when `count` records may go into one combined document, or the refusal. Both the
 * extension (before sending, with the count it has) and the API (whatever the caller) ask.
 */
export function combinedRecordCountRefusal(count: number): { error: string; code: typeof COMBINED_TOO_MANY_RECORDS; max: number } | null {
  if (count <= MAX_COMBINED_RECORDS) return null
  return {
    error: `One combined document takes at most ${MAX_COMBINED_RECORDS} records; ${count} were selected.`,
    code: COMBINED_TOO_MANY_RECORDS,
    max: MAX_COMBINED_RECORDS,
  }
}

/**
 * The generation worker's refusal of a combined document its memory cannot hold
 * (`expandCombinedBody()` in docx/combinedLayout.ts, whose test pins the wording): the
 * records asked for and the records that fit, or null for any other error. The API passes
 * the English sentence on as it is; the extension shows it in the panel's language.
 */
export function combinedTooLarge(error: string | null | undefined): { count: number; fit: number } | null {
  const m = /too large: (\d+) records of this template\b.*\bUse at most (\d+) records per document with this template/.exec(error ?? '')
  return m ? { count: Number(m[1]), fit: Number(m[2]) } : null
}

/** What the generation worker is told about a combined run (see CreateJobRequest.combined) */
export interface CombinedJobConfig {
  mode: CombinedRecordMode
  /** continuous only */
  pageBreak?: boolean
  /** labels only */
  labels?: LabelLayout
}

/**
 * The output a combined document is made in: PNG is LibreOffice's export of the first page
 * only, which would silently drop every record but the first sheet's — so a PDF instead.
 */
export function combinedOutputFormat<T extends string>(format: T, combined: CombinedJobConfig | undefined): T | 'pdf' {
  return combined && format === 'png' ? 'pdf' : format
}

/** The job's combined block for a template, or undefined for one document per record */
export function combinedJobConfig(mode: RecordMode, config: CombinedConfig): CombinedJobConfig | undefined {
  if (mode === 'per_record') return undefined
  if (mode === 'continuous') return { mode, pageBreak: config.pageBreak !== false }
  if (mode === 'labels') return { mode, labels: effectiveLabelLayout(config) }
  return { mode }
}

// =============================================================================
// Worker capabilities
// =============================================================================

/**
 * What a generation worker lists under `capabilities` in GET /health. The worker is deployed
 * separately and may be older than the API; the API sends neither a combined
 * job nor a template with `{ATTACH:…}` to one that does not list the feature.
 */
export const WORKER_CAPABILITY_COMBINED = 'combined'
export const WORKER_CAPABILITY_ATTACHMENTS = 'attachments'

export const COMBINED_UNAVAILABLE = {
  error: 'Combined documents cannot be generated right now. Please try again later.',
  code: 'COMBINED_UNAVAILABLE',
} as const

export const ATTACHMENTS_UNAVAILABLE = {
  error: 'This template puts attachments in the PDF, which cannot be generated right now. Please try again later.',
  code: 'ATTACHMENTS_UNAVAILABLE',
} as const

// =============================================================================
// Attachments in the PDF
// =============================================================================

/**
 * True when a template's stored placeholder tree (templates.placeholders) has an
 * `{ATTACH:…}` in it. Only a worker that lists `attachments` validates it as one, so a
 * template checked by an older worker reads false here — and that worker prints the tag
 * as text, which the template's owner saw in the validation warning at upload.
 */
export function templateUsesAttachments(placeholdersJson: string | null | undefined): boolean {
  if (!placeholdersJson) return false
  try {
    const walk = (nodes: unknown): boolean =>
      Array.isArray(nodes) && nodes.some(n => {
        const node = n as { fieldType?: unknown; nested?: unknown }
        return node?.fieldType === 'attachment' || walk(node?.nested)
      })
    return walk(JSON.parse(placeholdersJson))
  } catch {
    return false
  }
}

/** `generation_runs.context` of a combined run: every record, in print order */
export interface CombinedRunContext {
  combined: { mode: CombinedRecordMode; recordIds: string[] }
}
